import { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ErrorType,
  WorkoutPlanView,
} from "@/types";
import { Option } from "@/components/ui/autocomplete";
import { Difficulty } from "@/types/enums";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { initialValue, displayValue } from "@/lib/helper";
import { SearchCriteria } from "@/lib/constants/workout";
import {
  difficultyOptions,
  visibleFor,
  workoutGoals,
  workoutLevels,
} from "@/lib/constants/workout";
import {
  useGetAllWorkoutQuery,
  useUpdateWorkoutgridMutation,
} from "@/services/workoutApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import Pagination from "@/components/ui/table/pagination-table";
import usePagination from "@/hooks/use-pagination";
import { Badge } from "@/components/ui/badge";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
export default function WorkoutPlansTableView() {
  const workout = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).workout ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const navigate = useNavigate();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { toast } = useToast();
  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    data: workoutdata,
    isLoading,
    refetch,
    error,
    isError,
  } = useGetAllWorkoutQuery(
    {
      org_id: orgId,
      query: query,
    },
    {
      skip: query == "",
    }
  );

  useEffect(() => {
    if (isError && navigator.onLine) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: typedError.data?.detail ?? typedError.data?.message,
      });
    }
  }, [isError]);

  const WorkoutTableData = useMemo(() => {
    return (workoutdata?.data ?? []) as WorkoutPlanView[];
  }, [workoutdata]);
  const [updateGrid] = useUpdateWorkoutgridMutation();

  type UpdatePayload = {
    id: number;
    weeks: number;
    level?: Difficulty;
    goals?: string;
    org_id: number;
  };

  const handleUpdate = async (
    payload: UpdatePayload,
    updateType: "level" | "goal"
  ) => {
    try {
      const resp =
        updateType === "level"
          ? await updateGrid({
            org_id: payload.org_id,
            level: payload.level!,
            id: payload.id,
            weeks: payload.weeks,
          }).unwrap()
          : await updateGrid({
            org_id: payload.org_id,
            goals: payload.goals!,
            id: payload.id,
            weeks: payload.weeks,
          }).unwrap();

      refetch();

      if (resp) {
        toast({
          variant: "success",
          title: "Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleEditClick = (data: WorkoutPlanView & { id: number }) => {
    // Navigate to the edit route with the specific ID
    navigate(`/admin/workoutplans/add/step/1/${data.id}?mode=edit`);
  };

  const actionsColumn: ColumnDef<WorkoutPlanView> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={workout}
        row={row.original.id}
        data={row?.original}
        refetch={refetch}
      />
    ),
  };
  const columns: ColumnDef<WorkoutPlanView>[] = [
    {
      accessorKey: "Plan name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Plan Name"
          sortKey="plan_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-nowrap">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer">
                    {displayValue(
                      `${row.original.workout_name}`.length > 8
                        ? `${row.original.workout_name}`.substring(0, 8) + "..."
                        : `${row.original.workout_name}`
                    )}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{displayValue(`${row?.original?.workout_name}`)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      accessorKey: "goals",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Goal"
          sortKey="goals"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const id = Number(row.original.id);
        const weeks = Number(row.original.weeks);
        let goals = row.original.goals;
        return (
          <Select
            value={goals}
            onValueChange={(newValue) => {
              handleUpdate(
                { org_id: orgId, id: id, weeks: weeks, goals: newValue },
                "goal"
              );
            }}
            disabled={workout === "read"}
          >
            <SelectTrigger className="w-[200px] h-8">
              <SelectValue className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span>{goals}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {workoutGoals.map(
                (item: any) =>
                  !item.hide && (
                    <SelectItem key={item.value} value={item.label}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "level",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Level"
          sortKey="level"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const id = Number(row.original.id);
        const weeks = Number(row.original.weeks);
        const level = row.original.level;
        return (
          <Select
            defaultValue={level as Difficulty}
            onValueChange={(e: Difficulty) => {
              if (e) {
                handleUpdate(
                  { org_id: orgId, id: id, weeks: weeks, level: e },
                  "level"
                );
              }
            }}
            disabled={workout === "read"}
          >
            <SelectTrigger className="w-[150px] h-8">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span>{level}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {workoutLevels.map(
                (item: any) =>
                  !item.hide && (
                    <SelectItem key={item.value} value={item.label}>
                      {item.label}
                    </SelectItem>
                  )
              )}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "visible_for",
      meta: "Visible For",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Visible For"
          sortKey="visible_for"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex px-2 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row.original.visible_for)}
          </div>
        );
      },
    },

    {
      accessorKey: "week",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Week (X)"
          sortKey="weeks"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original?.weeks.toString())}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        const statusMap: Record<string, { label: string; color: string }> = {
          published: { label: "Published", color: "bg-green-600" },
          draft: { label: "Unpublished", color: "bg-blue-500" },
          archived: { label: "Archived", color: "bg-red-600" },
          clone: { label: "Clone", color: "bg-gray-400" },
        };
        const { label, color } = statusMap[status] ?? {
          label: "Unpublished",
          color: "bg-blue-500",
        };
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap ">
            <Badge className={`${color} text-white font-medium`}>
              {displayValue(label)}
            </Badge>{" "}
          </div>
        );
      },
    },
    ...(workout !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: WorkoutTableData as WorkoutPlanView[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
  });

  const handleOpen = () => {
    navigate("/admin/workoutplans/add/step/1?mode=add");
  };

  const totalRecords = workoutdata?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteria>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });
  interface Filter {
    visible_for?: string;
    goals?: string[];
    level?: string;
    statuses?: string[];
  }

  type FilterDisplayItem =
    | {
      type: "select";
      name: "visible_for" | "level";
      label: string;
      options?: Option[];
      function: (value: string) => void;
    }
    | {
      type: "multiselect";
      name: "goals" | "statuses";
      label: string;
      options?: { value: string; label: string }[];
      function: (value: string[]) => void;
    };

  const handleFilterChange = <T extends keyof Filter>(
    key: T,
    value: Filter[T]
  ) => {
    setFilterData((prev: Filter) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFilterChangeGoal = <T extends keyof Filter>(
    key: T,
    labels: Filter[T]
  ) => {
    setFilterData((prev: Filter) => ({
      ...prev,
      [key]: labels,
    }));
  };
  const filterDisplay = [
    {
      type: "multiselect",
      name: "goals",
      label: "Goals",
      options: workoutGoals.map((item) => ({
        value: item.label,
        label: item.label,
      })),
      function: (labels: string[]) => handleFilterChangeGoal("goals", labels),
    },
    {
      type: "select",
      name: "visible_for",
      label: "Visible For",
      options: visibleFor.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("visible_for", value),
    },
    {
      type: "select",
      name: "level",
      label: "Level",
      options: difficultyOptions.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("level", value),
    },
    {
      type: "multiselect",
      name: "statuses",
      label: "Statuses",
      options: [
        { value: "published", label: "Published" },
        { value: "draft", label: "Unpublished" },
        { value: "archived", label: "Archived" },
      ],
      function: (value: string[]) => handleFilterChange("statuses", value),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          {workout !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
              onClick={() => handleOpen()}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline ml-1">Create New</span>
            </Button>
          )}
          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : WorkoutTableData.length > 0 ? (
          WorkoutTableData.map((row: any) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => workout !== "read" && handleEditClick(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-lg capitalize">{row.workout_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      className={`${row.is_published ? "bg-green-600" : "bg-blue-500"} text-white font-medium`}
                    >
                      {row.is_published ? "Published" : "Unpublished"}
                    </Badge>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    access={workout}
                    row={row.id}
                    data={row}
                    refetch={refetch}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-y-3 text-sm pt-2">
                <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                  <span className="text-gray-400 font-medium">Goal:</span>
                  <Select
                    value={row.goals}
                    onValueChange={(newValue) => {
                      handleUpdate(
                        { org_id: orgId, id: row.id, weeks: row.weeks, goals: newValue },
                        "goal"
                      );
                    }}
                    disabled={workout === "read"}
                  >
                    <SelectTrigger className="w-full h-8">
                      <SelectValue className="text-gray-400">
                        <span className="flex gap-2 items-center">
                          <span>{row.goals}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {workoutGoals.map(
                        (item: any) =>
                          !item.hide && (
                            <SelectItem key={item.value} value={item.label}>
                              {item.label}
                            </SelectItem>
                          )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                  <span className="text-gray-400 font-medium">Level:</span>
                  <Select
                    defaultValue={row.level as Difficulty}
                    onValueChange={(e: Difficulty) => {
                      if (e) {
                        handleUpdate(
                          { org_id: orgId, id: row.id, weeks: row.weeks, level: e },
                          "level"
                        );
                      }
                    }}
                    disabled={workout === "read"}
                  >
                    <SelectTrigger className="w-full h-8">
                      <SelectValue placeholder="Status" className="text-gray-400">
                        <span className="flex gap-2 items-center">
                          <span>{row.level}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {workoutLevels.map(
                        (item: any) =>
                          !item.hide && (
                            <SelectItem key={item.value} value={item.label}>
                              {item.label}
                            </SelectItem>
                          )
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1 pt-1 border-t text-xs text-gray-500">
                  <div>
                    <strong>Visible For:</strong> {row.visible_for || "N/A"}
                  </div>
                  <div>
                    <strong>Weeks (X):</strong> {row.weeks}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No workout found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none border border-border ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (workout !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEditClick(row.original)
                      }
                    }}
                    className={workout !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : totalRecords ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No workout added yet.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No worktout found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {WorkoutTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
    </div>
  );
}
